/**
 * Active Breaks Page (PR 1, Phase B1)
 *
 * Real-time view of all currently-active breaks across the team.
 * Polls /api/breaks/team-breaks every 30 seconds. Admin/TL can
 * force-end any active break via the row action.
 *
 * Route: /admin/hr/breaks/active
 * RBAC:  admin / team_leader / tax_team_leader / title_team_leader / manager
 */

import React, { useState, useEffect, useCallback } from 'react';
import { isHrReadOnly } from '../../lib/hrRole';
import axios from 'axios';
import { RefreshCw, Square } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { BreakEmptyState, BreakPageHeader, BreakTableCard, BreakLoadingState, BreakErrorState, BreakStatusPill, BreakAvatar } from '../../components/hr/break/ui/BreakUi';
import { ForceEndBreakModal } from '../../components/hr/break/ForceEndBreakModal';
import {
  formatDuration,
  PaidUnpaidPill,
  formatDateTimeShortET,
} from '../../components/hr/break/breakUiHelpers';

const API = process.env.REACT_APP_BACKEND_URL || '';
const POLL_INTERVAL_MS = 30_000;

const ActiveBreaksPage = () => {
  const hrReadOnly = isHrReadOnly();
  const [breaks, setBreaks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [targetBreak, setTargetBreak] = useState(null);
  const [error, setError] = useState(null);

  const fetchActive = useCallback(async (isPoll = false) => {
    if (isPoll) setRefreshing(true);
    else setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(`${API}/api/breaks/team-breaks`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBreaks(resp.data.active_breaks || []);
      setLastRefresh(new Date());
      setError(null);
    } catch (err) {
      console.error('Failed to load active breaks:', err);
      setError(err?.response?.data?.detail || err.message || 'Failed to load active breaks');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchActive(false);
    const interval = setInterval(() => fetchActive(true), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchActive]);

  const handleForceEndSuccess = () => {
    setTargetBreak(null);
    fetchActive(false);
  };

  return (
    <>
      <BreakPageHeader title="Currently Active Breaks" subtitle="Live view of all team members currently on break. Auto-refreshes every 30 seconds." testId="active-header"
        action={
          <div className="flex items-center gap-3">
            {lastRefresh && (
              <span className="text-[11px] text-slate-400 inline-flex items-center gap-1.5" data-testid="active-last-refresh">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 inline-block" style={{ animation: 'liveops-pulse 1.6s infinite' }} />
                {/* eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional browser-local refresh clock */}
                Last updated {lastRefresh.toLocaleTimeString()}
              </span>
            )}
            <Button variant="outline" size="sm" onClick={() => fetchActive(true)} disabled={refreshing} data-testid="active-refresh-btn" className="bm-btn h-8 rounded-lg">
              <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        } />

      {loading ? (
        <BreakTableCard><BreakLoadingState label="Loading active breaks…" testId="active-loading" /></BreakTableCard>
      ) : error ? (
        <BreakErrorState title="Couldn't load active breaks." detail={error} onRetry={() => fetchActive(false)} testId="active-error" />
      ) : breaks.length === 0 ? (
        <BreakEmptyState title="No Active Breaks" subtitle="All team members are currently working."
          // eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional browser-local refresh clock
          meta={`Auto-refreshes every 30 seconds${lastRefresh ? ` · Last updated ${lastRefresh.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : ''}`} testId="active-empty" />
      ) : (
        <BreakTableCard testId="active-table-wrapper">
          <Table className="tf-break-table">
            <TableHeader>
              <TableRow className="bm-thead">
                <TableHead>Employee</TableHead>
                <TableHead>Break Type</TableHead>
                <TableHead>Paid?</TableHead>
                <TableHead>Started</TableHead>
                <TableHead className="w-[260px]">Progress</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {breaks.map((b) => {
                const elapsed = b.elapsed_minutes || 0;
                const cap = b.max_duration_minutes || 0;
                const pct = cap > 0 ? Math.min(100, Math.round((elapsed / cap) * 100)) : 0;
                const isOver = b.is_over_limit || (cap > 0 && elapsed > cap);
                const isNear = !isOver && pct >= 80;
                const barColor = isOver ? 'bg-rose-600' : isNear ? 'bg-amber-500' : 'bg-emerald-500';
                const trackColor = isOver ? 'bg-rose-200' : 'bg-slate-100';
                return (
                  <TableRow key={b.id} data-testid={`active-row-${b.id}`} className={`bm-row ${isOver ? 'bg-rose-50/60' : ''}`}>
                    <TableCell className="font-medium"><span className="flex items-center gap-2.5"><BreakAvatar name={b.user_name || b.user_id} tone="emerald" />{b.user_name || b.user_id}</span></TableCell>
                    <TableCell>{b.break_type_name}</TableCell>
                    <TableCell><PaidUnpaidPill isPaid={b.is_paid} /></TableCell>
                    <TableCell className="text-xs text-gray-600">
                      {b.start_time ? formatDateTimeShortET(b.start_time) : '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className={`flex-1 h-1.5 ${trackColor} rounded-full overflow-hidden`}>
                          <div
                            className={`h-full ${barColor} transition-all duration-500`}
                            style={{ width: `${isOver ? 100 : pct}%` }}
                            data-testid={`active-row-${b.id}-bar`}
                          />
                        </div>
                        <span className="font-mono text-xs whitespace-nowrap">
                          <span className={`font-semibold ${isOver ? 'text-rose-700' : isNear ? 'text-amber-700' : 'text-emerald-700'}`}>
                            {formatDuration(elapsed)}
                          </span>
                          <span className="text-slate-400"> / {formatDuration(cap)}</span>
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {isOver ? <BreakStatusPill tone="red" dot testId="active-status-pill">Over Cap +{Math.max(0, elapsed - cap)}m</BreakStatusPill>
                        : isNear ? <BreakStatusPill tone="amber" dot testId="active-status-pill">Near Cap</BreakStatusPill>
                        : <BreakStatusPill tone="green" dot testId="active-status-pill">Active</BreakStatusPill>}
                    </TableCell>
                    <TableCell className="text-right">
                      {hrReadOnly ? (
                        <span className="text-xs text-slate-400" data-testid={`force-end-readonly-${b.id}`}>View only</span>
                      ) : (
                      <Button
                        variant={isOver ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setTargetBreak(b)}
                        className={`bm-btn h-8 rounded-lg ${isOver
                          ? 'bg-rose-600 hover:bg-rose-700 text-white'
                          : 'border-rose-300 text-rose-700 hover:bg-rose-50'}`}
                        data-testid={`force-end-btn-${b.id}`}
                      >
                        <Square className="w-3.5 h-3.5 mr-1" />
                        {isOver ? 'End Now' : 'Force-End'}
                      </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </BreakTableCard>
      )}

      <ForceEndBreakModal
        open={!!targetBreak}
        onOpenChange={(v) => !v && setTargetBreak(null)}
        breakRecord={targetBreak}
        onSuccess={handleForceEndSuccess}
      />
    </>
  );
};

export default ActiveBreaksPage;
